import React, { memo, useMemo } from 'react'
import { getPropType } from '@based/sdk/schema'
import { BooleanType } from './Cases/BooleanType.js'
import { EnumType } from './Cases/EnumType.js'
import { JsonType } from './Cases/JsonType.js'
import { NumberType } from './Cases/NumberType.js'
import { ReferenceType } from './Cases/ReferenceType.js'
import { StringType } from './Cases/StringType.js'
import { TimeStampType } from './Cases/TimeStampType.js'
import { BinaryType } from './Cases/BinaryType.js'
import { ObjectType } from './Cases/ObjectType.js'
import { Note } from '../Note/index.js'
import { useLanguage } from '../LanguageSelect/index.js'

type SchemaFormComponentProps = {
  schemaProp: any
  name?: string
  value: any
  onChange?: (value: any, name: any) => void
  disabled?: boolean
  checksum?: number
}

export const SchemaFormComponent: React.FC<SchemaFormComponentProps> = memo(
  ({ schemaProp, name, value, onChange, checksum, disabled }) => {
    if (!schemaProp) return null

    const [lang] = useLanguage()
    const schemaPropType = getPropType(schemaProp)

    let newValue = value
    if (
      newValue &&
      typeof newValue === 'object' &&
      !Array.isArray(newValue) &&
      newValue?.[lang] !== undefined
    ) {
      newValue = newValue[lang]
    }

    const commonProps = {
      value: newValue,
      onChange,
      name,
      schemaProp,
      disabled,
    }

    let component: any = null

    switch (schemaPropType) {
      case 'string':
      case 'text':
      case 'alias': {
        component = <StringType {...commonProps} />
        break
      }
      case 'number': {
        component = <NumberType int={false} {...commonProps} />
        break
      }
      case 'boolean': {
        component = <BooleanType {...commonProps} />
        break
      }
      case 'timestamp': {
        component = <TimeStampType {...commonProps} />
        break
      }
      case 'json': {
        component = <JsonType {...commonProps} />
        break
      }
      case 'reference': {
        component = <ReferenceType variant="single" {...commonProps} />
        break
      }
      case 'references': {
        component = <ReferenceType variant="multiple" {...commonProps} />
        break
      }
      case 'enum': {
        component = <EnumType {...commonProps} />
        break
      }
      case 'object': {
        component = <ObjectType {...commonProps} />
        break
      }
      case 'binary': {
        component = <BinaryType {...commonProps} />
        break
      }
      case 'int8': {
        component = <NumberType type="int8" {...commonProps} />
        break
      }
      case 'int16': {
        component = <NumberType type="int16" {...commonProps} />
        break
      }
      case 'int32': {
        component = <NumberType type="int32" {...commonProps} />
        break
      }
      case 'uint8': {
        component = <NumberType type="uint8" {...commonProps} />
        break
      }
      case 'uint16': {
        component = <NumberType type="uint16" {...commonProps} />
        break
      }
      case 'uint32': {
        component = <NumberType type="uint32" {...commonProps} />
        break
      }
      case 'cardinality':
      case 'vector':
      case 'colvec': {
        component = <></>
        break
      }
      default: {
        console.warn(`Unknown schema property type: ${schemaPropType}`)
        component = null
      }
    }

    if (schemaProp?.note) {
      return (
        <>
          {component}
          <Note
            title={schemaProp.note?.title}
            description={schemaProp.note?.description}
          />
        </>
      )
    }

    return component
  },
  (prev, next) =>
    prev.value === next.value &&
    prev.disabled === next.disabled &&
    prev.schemaProp === next.schemaProp &&
    prev.name === next.name &&
    prev.checksum === next.checksum,
)
